import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { InfoIcon } from "lucide-react";
import { BELONGS_TO } from "creso-core/constants";
import { SecondHolderContactDetailsSchema } from "creso-core/zod";
import { splitHolderPhone } from "creso-core/utils";
import { AddSecondHolderFormData } from "creso-core/types";
import {
  Button,
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
  FormSelect,
  useMultiStepForm,
} from "web-design-system";
import { useSaveSecondHolderProfile } from "@crm/hooks/use-second-holder";

export function ContactDetailsStep() {
  const { formData, previousStep } =
    useMultiStepForm<AddSecondHolderFormData>();
  const { saveAndContinue, isPending, secondHolder } =
    useSaveSecondHolderProfile();
  const existingPhone = splitHolderPhone(secondHolder?.phone_number);
  const isLinkedToExistingClient =
    !!formData.linked_client_id || !!formData.has_other_client;
  // Existing client's values are read-only; missing ones can still be filled
  const lockIfFilled = (value?: string | null) =>
    isLinkedToExistingClient && !!value;

  const form = useForm<z.infer<typeof SecondHolderContactDetailsSchema>>({
    resolver: zodResolver(SecondHolderContactDetailsSchema),
    values: {
      number: formData.number ?? existingPhone.number,
      email: formData.email ?? secondHolder?.email ?? "",
      phone_belongs_to: formData.phone_belongs_to ?? "self",
      email_belongs_to: formData.email_belongs_to ?? "self",
      isd: formData.isd ?? existingPhone.isd,
    },
  });

  const onSubmit = (data: z.infer<typeof SecondHolderContactDetailsSchema>) => {
    return saveAndContinue(data);
  };

  return (
    <Form {...form}>
      <form
        className="flex min-h-0 flex-1 flex-col gap-6 pt-3"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <span className="text-lg font-semibold">Contact Details</span>
        <div className="flex flex-col gap-10">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="number"
              render={({ field }) => (
                <FormPrefixedInput
                  {...field}
                  prefix=""
                  label="Mobile"
                  type="tel"
                  maxLength={10}
                  placeholder="9999999999"
                  disabled={lockIfFilled(formData.number)}
                  onChange={(val) =>
                    field.onChange(
                      String(val ?? "")
                        .replace(/\D/g, "")
                        .slice(0, 10)
                    )
                  }
                >
                  +91
                </FormPrefixedInput>
              )}
            />
            <FormField
              control={form.control}
              name="phone_belongs_to"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Mobile number belongs to"
                  options={BELONGS_TO}
                  disabled={lockIfFilled(formData.phone_belongs_to)}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormInput
                  {...field}
                  label="Email"
                  disabled={lockIfFilled(formData.email)}
                />
              )}
            />
            <FormField
              control={form.control}
              name="email_belongs_to"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Email belongs to"
                  options={BELONGS_TO}
                  disabled={lockIfFilled(formData.email_belongs_to)}
                />
              )}
            />
          </div>
        </div>
        <div className="mt-auto flex flex-col gap-4">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-tag-slate p-3 text-tag-slate-foreground">
            <InfoIcon className="size-4 shrink-0 stroke-tag-slate-foreground" />
            <span className="text-xs">
              Mark as self only if email and phone belongs to the client
            </span>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={previousStep}>
              Previous
            </Button>
            <Button type="submit" disabled={isPending}>
              Next
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
